import { Badge, Button, Dropdown, Empty, Grid, Space, Tooltip, Typography } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Notification01Icon } from '@hugeicons/core-free-icons';
import { Icon } from '@/components/index.js';
import { NotificationRow } from './NotificationRow.js';
import { space } from '@/design/tokens.js';
import { useCloseOnScroll } from '@/lib/ui/close-on-scroll.js';
import {
  notificationsQuery,
  useMarkRead,
  useNotifications,
  useUnreadCount,
  type Notification,
  useOpenNotification,
} from './notifications.api.js';

const { Text } = Typography;

/**
 * The bell, and what drops out of it.
 *
 * The badge polls a counting endpoint; the list is fetched when the dropdown
 * opens, and **warmed whenever the count changes**. Loading thirty rows every
 * minute to render one number would be a waste of both ends, and the dropdown
 * is shut almost all of the time — but fetching them once because something
 * actually arrived is the difference between opening the bell and seeing the
 * new notification, and opening it to the old set with a pause after it.
 *
 * Opening a notification marks that one read and follows its link. Reading
 * something is what "read" means — making people tick a box afterwards is
 * bookkeeping nobody asked for.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);

  /*
    Same panel, same cause as the account menu: positioned once, against a
    button the page can scroll away from under it (orbit-ui#179).

    The ref is what stops it closing when somebody scrolls *inside* the
    panel to read older notifications (orbit-ui#180) — that scroll does not
    move the panel relative to its button, so there is nothing to correct.
  */
  const panel = useRef<HTMLDivElement>(null);
  useCloseOnScroll(
    open,
    useCallback(() => setOpen(false), []),
    panel,
  );
  const badge = useUnreadCount();
  const list = useNotifications(open);
  const markRead = useMarkRead();
  // Above the compact early return below: hooks run in the same order every
  // render or not at all.
  const follow = useOpenNotification();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const items = list.data?.items ?? [];
  const unread = badge.data?.unreadCount ?? 0;

  /**
   * On a phone the bell is a link, not a menu.
   *
   * A 360px panel anchored to the top-right corner of a 375px screen has
   * nowhere to go: it covers the page it is supposed to preview, and every
   * route out of it is a second tap. The page it previews is a better answer
   * at that width, so the bell simply opens it. Reported as orbit-ui#21.
   *
   * The same `lg` breakpoint the shell uses to decide it is compact, so the
   * two cannot disagree about what a phone is.
   */
  const isCompact = !Grid.useBreakpoint().lg;

  /**
   * Fetch the list when the count moves, not when the dropdown opens.
   *
   * Opening it is the moment somebody is waiting; the count changing is not.
   * Doing the work at the second means the first has nothing left to wait for.
   */
  useEffect(() => {
    if (!badge.isSuccess) return;
    void queryClient.prefetchQuery(notificationsQuery);
  }, [unread, badge.isSuccess, queryClient]);

  const bell = (
    <Tooltip title="Notifications">
      {/* `orbit-badge-inside` keeps the count within the button. By default a
          badge is pinned to the right edge and then pushed out by half its own
          width, so it grew into the profile button as soon as the count
          reached two digits. */}
      <Badge className="orbit-badge-inside" count={unread} size="small" offset={[-4, 4]}>
        <Button
          type="text"
          aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
          icon={<Icon icon={Notification01Icon} size="lg" />}
          onClick={isCompact ? () => void navigate('/notifications') : undefined}
        />
      </Badge>
    </Tooltip>
  );

  // On a phone the bell *is* the link. Wrapping it in a dropdown that is told
  // never to open would still swallow the click.
  if (isCompact) return bell;

  const openOne = (item: Notification) => {
    setOpen(false);
    follow(item);
  };

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={['click']}
      placement="bottomRight"
      dropdownRender={() => (
        <div
          ref={panel}
          style={{
            width: 360,
            maxHeight: 420,
            overflowY: 'auto',
            background: 'var(--ant-color-bg-elevated)',
            borderRadius: 8,
            boxShadow: 'var(--ant-box-shadow-secondary)',
            padding: space[2],
          }}
        >
          <Space
            style={{ width: '100%', justifyContent: 'space-between', padding: space[2] }}
            align="center"
          >
            <Text strong>Notifications</Text>
            {unread > 0 && (
              <Button
                size="small"
                type="link"
                loading={markRead.isPending}
                onClick={() => markRead.mutate(undefined)}
              >
                Mark all read
              </Button>
            )}
          </Space>

          {/* `isFetching`, not `isLoading`: on a reopen there is cached data,
              so `isLoading` is false and the old list would sit there saying
              nothing while the new one arrived. */}
          {list.isFetching && (
            <Text type="secondary" style={{ padding: space[2] }}>
              Loading…
            </Text>
          )}

          {list.isSuccess && items.length === 0 && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Nothing yet"
              style={{ padding: space[4] }}
            />
          )}

          {items.map((item) => (
            <NotificationRow key={item.id} item={item} onOpen={openOne} />
          ))}

          <Button
            type="link"
            block
            onClick={() => {
              setOpen(false);
              void navigate('/notifications');
            }}
          >
            See all
          </Button>
        </div>
      )}
    >
      {bell}
    </Dropdown>
  );
}
